import { Box, BoxContent } from '@mm/components';
import { ADS_ANALYTICS_QUERY_INFO } from '@mm/constants/ctm-analytics';
import { useAdAccountCRUDActions } from '@mm/state/actions/AdAccountCRUDActions';
import { useCtmInsightsCRUDActions } from '@mm/state/actions/CtmInsightsCRUDActions';
import JSONUtils from '@mm/state/helpers/json-utils';
import {
  CtmAnalyticsQuery,
  queryRangeDateFormat,
} from '@mm/state/model/CtmAnalyticsModel';
import { analyticsRecordsForQuery } from '@mm/state/selectors/ctmInsightsSelectors';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { LocalStorageService } from '@mm/utils';
import classNames from 'classnames';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { DEFAULT_CURRENCY } from '../../../../../common/utils';
import './AdsAnalytics.pcss';
import { AdsAnalyticsQueryBuilder } from './AdsAnalyticsQueryBuilder/AdsAnalyticsQueryBuilder';
import { AdsAnalyticsTable } from './AdsAnalyticsTable/AdsAnalyticsTable';

type Props = {
  className?: string;
};

type LSQuery = {
  since: string;
  until: string;
  parentId: string;
};

const retrieveDefaultQueries = () =>
  JSONUtils.safeParse<{ [key: string]: LSQuery }>(
    LocalStorageService.getItem(ADS_ANALYTICS_QUERY_INFO),
  ) || {};

export const AdsAnalytics = ({ className }: Props) => {
  const botId = useSelector(activeChatbotIdSelector);
  const history = useHistory();
  const hasAccessToAdsAnalytics = useSelector(
    combinedFeatureFlagsSelector,
  ).analyticsAds;
  if (!hasAccessToAdsAnalytics) {
    const pathname = BotBuilderRoutes.dashboard.getLink({ chatbotId: botId });
    history.push(pathname);
  }
  const adAccountsCRUDActions = useAdAccountCRUDActions();
  const ctmInsightsCRUDActions = useCtmInsightsCRUDActions();

  const [query, setQuery] = useState<CtmAnalyticsQuery | null>(null);

  const adAccounts = useSelector(
    (state: StoreState) => state.adAccounts.entities,
  );
  const currency =
    adAccounts.find(a => a.id === query?.parentId)?.currency ||
    DEFAULT_CURRENCY;
  const records = useSelector((state: StoreState) =>
    analyticsRecordsForQuery(state, query),
  );
  const fbPageId = useSelector(activeFacebookPageIdSelector);
  const defaultQuery = retrieveDefaultQueries()[fbPageId];

  const onChangePage = useCallback(
    (token: string) =>
      setQuery(query => (query === null ? null : { ...query, token })),
    [setQuery],
  );

  useEffect(() => {
    (async () => {
      let accounts;
      if (!adAccounts || adAccounts.length === 0) {
        accounts = await adAccountsCRUDActions.retrieveAll(
          fbPageId,
          false,
          '',
          true,
        );
      } else {
        accounts = adAccounts;
      }
      const account =
        accounts.find(a => a.id === defaultQuery?.parentId) || accounts[0];
      setQuery({
        token: null,
        query: null,
        parentId: account !== undefined ? account.id : null,
        range: {
          since: defaultQuery?.since
            ? moment(defaultQuery.since)
            : moment(['2020']),
          until: defaultQuery?.until ? moment(defaultQuery.until) : moment(),
        },
      });
    })();
  }, []);

  useEffect(() => {
    if (query !== null && records.data.length === 0 && query.parentId) {
      ctmInsightsCRUDActions.retrieveForQuery(botId, query);
    }
  }, [query]);

  return (
    <div className="bot-analytics__wrapper">
      <div className="bot-analytics__header">
        Ads Analytics
        <AdsAnalyticsQueryBuilder
          query={query}
          setQuery={query => {
            const storageQuery = {
              since: query.range.since.format(queryRangeDateFormat),
              until: query.range.until.format(queryRangeDateFormat),
              parentId: query.parentId,
            };
            const lsQuery = {
              ...retrieveDefaultQueries(),
              [fbPageId]: storageQuery,
            };
            setQuery(query);
            LocalStorageService.setItem(
              ADS_ANALYTICS_QUERY_INFO,
              JSON.stringify(lsQuery),
            );
          }}
          adAccounts={adAccounts}
        />
      </div>
      <section className={classNames(className, 'ads-analytics')}>
        <Box className="ads-analytics__box">
          <BoxContent className="ads-analytics__content">
            <div className="ads-analytics__content-data">
              <AdsAnalyticsTable
                records={records}
                onChangePage={onChangePage}
                currencyCode={currency}
              />
            </div>
          </BoxContent>
        </Box>
      </section>
    </div>
  );
};
